import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, Form, FormGroup, Label, Input, Button, FormFeedback, CardTitle } from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import * as yup from "yup";
import { useFormik } from "formik";
import { getBookingDocuments, updateCollectionFrom } from 'helpers/API Calls/Operations/booking';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from 'Components/UI/Spinner';

interface CollectionContent {
  equipment_damaged: boolean;
  equipment_wet: boolean;
  equipment_dirty: boolean;
  details: string;
}

interface CollectionDocument {
  id: number;
  booking_id: number;
  type: string;
  status: number;
  content: CollectionContent;
  created_at: string;
  updated_at: string;
}

function Collection() {
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  
  const formik = useFormik<CollectionContent>({
    initialValues: {
      equipment_damaged: false,
      equipment_wet: false,
      equipment_dirty: false,
      details: '',
    },
    validationSchema: yup.object().shape({
      details: yup.string().required('Details are required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const response = await updateCollectionFrom(Number(id), {...values});
      setSubmitting(false);
      if(response.success){
        navigate('/bookings');
      }
    },
  });

  useEffect(() => {
    
    const getForm = async () => {
      setLoading(true);
      try {
        const response = await getBookingDocuments('type=collection&limit=1&booking_id=' + id);
        if (response.success && response?.data?.items?.length > 0) {
          const document: CollectionDocument = response.data?.items?.[0];
          formik.setValues({
            equipment_damaged: document.content.equipment_damaged || false,
            equipment_wet: document.content.equipment_wet || false,
            equipment_dirty: document.content.equipment_dirty || false,
            details: document.content.details || '',
          });
        }
      } catch (error) {
        console.error('Error fetching collection document:', error);
      }
      setLoading(false);
    };

    getForm();
  }, [id])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Bookings", link: "/bookings" }, {label: 'Collection Document'}]} />
        {loading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Equipment Status Section */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Equipment Status</CardTitle>
                <FormGroup check className="mb-3">
                  <Input
                    type="checkbox"
                    id="equipment_damaged"
                    name="equipment_damaged"
                    checked={formik.values.equipment_damaged}
                    onChange={formik.handleChange}
                  />
                  <Label check htmlFor="equipment_damaged">
                    Equipment Damaged
                  </Label>
                </FormGroup>

                <FormGroup check className="mb-3">
                  <Input
                    type="checkbox"
                    id="equipment_wet"
                    name="equipment_wet"
                    checked={formik.values.equipment_wet}
                    onChange={formik.handleChange}
                  />
                  <Label check htmlFor="equipment_wet">
                    Equipment Wet
                  </Label>
                </FormGroup>

                <FormGroup check className="mb-3">
                  <Input
                    type="checkbox"
                    id="equipment_dirty"
                    name="equipment_dirty"
                    checked={formik.values.equipment_dirty}
                    onChange={formik.handleChange}
                  />
                  <Label check htmlFor="equipment_dirty">
                    Equipment Dirty
                  </Label>
                </FormGroup>
              </div>

              {/* Additional Details Section */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Additional Details</CardTitle>
                <FormGroup>
                  <Label htmlFor="details">Details <span className="text-danger">*</span></Label>
                  <Input
                    type="textarea"
                    id="details"
                    name="details"
                    placeholder="Enter details"
                    rows={4}
                    value={formik.values.details}
                    onChange={formik.handleChange}
                    invalid={formik.touched.details && formik.errors.details ? true : false}
                  />
                  {formik.errors.details && formik.touched.details && (
                    <FormFeedback type="invalid">{formik.errors.details}</FormFeedback>
                  )}
                </FormGroup>
              </div>

              <div className="d-flex mb-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>}
      </Container>
    </div>
  );
}

export default Collection;
